import type { ReactNode } from 'react'

import { ApiError, ErreurReseau } from '../../lib/api/erreurs'

/**
 * État vide **nommé et actionnable**.
 *
 * Un tableau vide muet laisse l'utilisateur se demander si la page a échoué,
 * si le filtre est trop restrictif, ou s'il n'y a réellement rien. On le lui
 * dit, et on lui donne l'action suivante.
 */
export function EtatVide({
  titre,
  description,
  action,
  icone = '📭',
}: {
  titre: string
  description?: string
  action?: ReactNode
  icone?: string
}) {
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
      <p className="text-4xl" aria-hidden="true">
        {icone}
      </p>
      <h2 className="mt-3 text-base font-semibold text-slate-800">{titre}</h2>
      {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

/**
 * Message d'erreur.
 *
 * Le `detail` du backend est **déjà en français** (§6) : on l'affiche tel
 * quel. Le retraduire ou le remplacer par un message générique ferait perdre
 * la seule information utile — ce qui a précisément échoué.
 */
export function MessageErreur({
  erreur,
  onReessayer,
}: {
  erreur: unknown
  onReessayer?: () => void
}) {
  const reseau = erreur instanceof ErreurReseau
  const api = erreur instanceof ApiError ? erreur : null

  return (
    <div
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
    >
      <p className="font-medium">
        {reseau ? 'Serveur injoignable' : api ? 'La demande a été refusée' : 'Une erreur est survenue'}
      </p>
      <p className="mt-1">
        {erreur instanceof Error ? erreur.message : 'Erreur inconnue.'}
      </p>
      {api?.code ? <p className="mt-1 font-mono text-xs text-red-700">{api.code}</p> : null}
      {onReessayer ? (
        <button
          type="button"
          onClick={onReessayer}
          className="mt-3 rounded border border-red-300 bg-white px-3 py-1.5 font-medium text-red-800 hover:bg-red-100"
        >
          Réessayer
        </button>
      ) : null}
    </div>
  )
}

/** Bandeau d'information neutre. */
export function Bandeau({
  ton = 'info',
  children,
}: {
  ton?: 'info' | 'attention' | 'succes'
  children: ReactNode
}) {
  const styles = {
    info: 'border-sky-200 bg-sky-50 text-sky-900',
    attention: 'border-amber-200 bg-amber-50 text-amber-900',
    succes: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  } as const
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${styles[ton]}`} role="status">
      {children}
    </div>
  )
}
